import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Typography,
  Stack,
  InputAdornment,
  Link as MuiLink,
} from '@mui/material';
import { Email as EmailIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import clsx from 'clsx';
import authApi from '../api';
import { useLoading } from '@/hooks/useLoading';
import FullScreenLoading from '@/components/common/FullLoading';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import { useAppStore } from '@/stores';
import { EMAIL_PATTERN } from '@/constants/auth';

interface ForgotPasswordFormData {
  email: string;
}

export default function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { loading, setLoading } = useLoading();
  const themeMode = useAppStore((state) => state.ui.themeMode);
  const isDark = themeMode === 'dark';

  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    mode: 'onBlur',
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: (data: { email: string }) => authApi.forgotPassword(data),
    onMutate: () => setLoading(true),
    onSuccess: () => {
      setIsSubmitted(true);
      toast.success(t('forgotPassword.successMessage'));
      setLoading(false);
    },
    onError: () => {
      // Still show success message for security (prevent email enumeration)
      setIsSubmitted(true);
      toast.success(t('forgotPassword.successMessage'));
      setLoading(false);
    },
  });

  const onSubmit = (data: ForgotPasswordFormData) => {
    forgotPasswordMutation.mutate({ email: data.email });
  };

  return (
    <Box
      className={clsx(
        'login-container',
        isDark ? 'login-container--dark' : 'login-container--light'
      )}
    >
      <FullScreenLoading open={loading} />
      {/* Theme and language toggle in top-right corner */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          zIndex: 10,
        }}
      >
        <LanguageSwitcher />
      </Box>
      <Card className={clsx('login-card', isDark ? 'login-card--dark' : 'login-card--light')}>
        <CardHeader
          sx={{ padding: 2 }}
          title={
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src={isDark ? '/intellio-wordmark-dark.svg' : '/intellio-wordmark-light.svg'}
                alt="Intellio AI"
                sx={{ height: 64, width: 'auto' }}
              />
            </Box>
          }
        />
        <CardContent>
          {!isSubmitted ? (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>
                  {t('forgotPassword.title')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ textAlign: 'center', color: 'text.secondary', mb: 2 }}
                >
                  {t('forgotPassword.description')}
                </Typography>

                <TextField
                  fullWidth
                  type="email"
                  placeholder={t('login.email')}
                  className="login-text-field"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                  {...register('email', {
                    required: t('login.validation.emailRequired'),
                    pattern: {
                      value: EMAIL_PATTERN,
                      message: t('login.validation.emailInvalid'),
                    },
                  })}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  className="login-button"
                  disabled={forgotPasswordMutation.isPending}
                >
                  {forgotPasswordMutation.isPending
                    ? t('forgotPassword.sending')
                    : t('forgotPassword.sendLink')}
                </Button>

                <Button
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/login')}
                  sx={{ mt: 2, textTransform: 'none' }}
                >
                  {t('forgotPassword.backToLogin')}
                </Button>
              </Stack>
            </form>
          ) : (
            <Stack spacing={2} sx={{ textAlign: 'center' }}>
              <Typography variant="h6">{t('forgotPassword.checkEmail')}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('forgotPassword.checkEmailDescription')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 2 }}>
                {t('forgotPassword.linkExpiry')}
              </Typography>
              <MuiLink
                href="/login"
                sx={{
                  cursor: 'pointer',
                  color: 'primary.main',
                  textDecoration: 'none',
                  mt: 2,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {t('forgotPassword.backToLogin')}
              </MuiLink>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
